import React, { useState, useEffect } from 'react';
import { DocumentService, EmbeddingStatsResponse } from '../utils/apiService';
import { FiFileText, FiCheckCircle, FiClock, FiSearch, FiRefreshCw } from 'react-icons/fi';
import { motion } from 'framer-motion';
import { Toaster, toast } from 'react-hot-toast';

// Import the DocumentStatusTable component
import EmbeddingProgressBar from '../components/document/EmbeddingProgressBar';
import DocumentStatusTable from '../components/document/DocumentStatusTable';

const DocumentsStatusPage: React.FC = () => {
  const [stats, setStats] = useState<EmbeddingStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [view, setView] = useState<'all' | 'embedded' | 'pending'>('all');

  const fetchStats = async () => {
    try {
      setLoading(true);
      const response = await DocumentService.getEmbeddingStats();
      setStats(response);
      setError(null);
    } catch (err: any) {
      console.error("Error fetching embedding stats:", err);
      setError(err.message || "Failed to fetch embedding statistics");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch on mount
  useEffect(() => {
    fetchStats();
  }, []);

  const handleEmbedNewDocuments = async () => {
    try {
      setLoading(true);
      toast.loading("Controllo nuovi documenti non indicizzati...");
      
      // Use the async version for better performance
      const result = await DocumentService.embedNewDocumentsAsync(5);
      
      if (result.success) {
        const successCount = result.successful_count || 0;
        if (successCount > 0) {
          toast.success(`Indicizzati ${successCount} nuovi documenti con successo`);
        } else {
          toast.success("Nessun nuovo documento da indicizzare trovato");
        }
      } else {
        toast.error(`Errore durante l'indicizzazione: ${result.message}`);
      }
      
      // Refresh stats
      await fetchStats();
    } catch (err: any) {
      console.error("Error embedding new documents:", err);
      toast.error(err.message || "Errore durante l'indicizzazione dei nuovi documenti");
    } finally {
      setLoading(false);
    }
  };



  const filteredDocuments = stats?.embedding_status?.filter(doc => {
    // Apply search filter
    const matchesSearch = searchTerm === '' || 
      doc.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (doc.file_path && doc.file_path.toLowerCase().includes(searchTerm.toLowerCase()));

    // Apply view filter
    if (view === 'all') return matchesSearch;
    if (view === 'embedded') return matchesSearch && doc.is_embedded;
    if (view === 'pending') return matchesSearch && !doc.is_embedded;
    
    return matchesSearch;
  }) || [];

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
      <main className="px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-end items-center mb-6">
            <button
              onClick={fetchStats}
              className="flex items-center px-4 py-2 bg-blue-50 text-blue-600 dark:bg-blue-900 dark:text-blue-200 rounded-md hover:bg-blue-100 dark:hover:bg-blue-800 transition-colors"
            >
              <FiRefreshCw className="mr-2" />
              Aggiorna
            </button>
          </div>

          {/* Progress overview */}
          <div className="mb-8">
            <EmbeddingProgressBar 
              refreshInterval={0}
              autoRefresh={false}
              onEmbed={handleEmbedNewDocuments} 
            />
          </div>

          {/* Status cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center">
                <div className="bg-green-100 dark:bg-green-900/30 p-3 rounded-full mr-4">
                  <FiCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Documenti Indicizzati</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.embedded_documents || 0}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center">
                <div className="bg-yellow-100 dark:bg-yellow-900/30 p-3 rounded-full mr-4">
                  <FiClock className="h-6 w-6 text-yellow-600 dark:text-yellow-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Documenti non indicizzati</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.pending_documents || 0}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              whileHover={{ scale: 1.02 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center">
                <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full mr-4">
                  <FiFileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Documenti Totali</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {stats?.total_documents || 0}
                  </p>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Search and filter bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div className="relative flex-1 max-w-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
                placeholder="Cerca documenti..."
              />
            </div>

            <div className="flex space-x-2">
              <button
                onClick={() => setView('all')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'all' 
                    ? 'bg-blue-600 text-white dark:bg-blue-700'
                    : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-700'
                }`}
              >
                Tutti
              </button>
              <button
                onClick={() => setView('embedded')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'embedded' 
                    ? 'bg-green-600 text-white dark:bg-green-700'
                    : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-700'
                }`}
              >
                Indicizzati
              </button>
              <button
                onClick={() => setView('pending')}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  view === 'pending' 
                    ? 'bg-yellow-600 text-white dark:bg-yellow-700'
                    : 'bg-white text-gray-700 dark:bg-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-700'
                }`}
              >
                Non indicizzati
              </button>
            </div>
          </div>

          {/* Documents status table */}
          {loading && !stats ? (
            <div className="w-full py-20 flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : error && !stats ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 p-4 rounded-lg">
              <p className="text-red-600 dark:text-red-400">
                Errore: {error}
              </p>
              <button
                onClick={fetchStats}
                className="mt-2 px-4 py-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-md hover:bg-red-200 dark:hover:bg-red-800 transition-colors"
              >
                Riprova
              </button>
            </div>
          ) : (
            <DocumentStatusTable 
              documents={filteredDocuments}
              onRefresh={fetchStats}
            />
          )}
          
          {/* Toast notifications container */}
          <Toaster 
            position="bottom-right"
            toastOptions={{
              duration: 5000,
              style: {
                background: 'rgb(var(--background))',
                color: 'rgb(var(--foreground))',
              },
            }}
          />
        </div>
      </main>
    </div>
  );
};

export default DocumentsStatusPage; 